import { TicketsFilters } from "@services/tickets/types";
import {
  asNumber,
  asString,
  objectToSearchParams,
  SLFilterToSearch,
} from "./common";
import { convertLocalTime } from "@common/utils/localTime.utils";
import { getMappedValue } from "./const";
import { Priority } from "@services/common/types";
import { convertCountryName } from "@assets/countries";
export const priorityMap: {
  [K in "critical" | "major" | "minor" | "informational" | Priority]: Priority;
} = {
  critical: Priority.Critical,
  Critical: Priority.Critical,
  major: Priority.Major,
  Major: Priority.Major,
  minor: Priority.Minor,
  Minor: Priority.Minor,
  informational: Priority.Informational,
  Informational: Priority.Informational,
};

export const TIME_FILTER_SEARCH_KEY = "time";

export const getTimesForTicketsFilter = (
  filter: TicketsTimeFilter,
  payload?: DateInterval
): [start?: number, end?: number] => {
  let [start, end] = [
    convertLocalTime(new Date()),
    convertLocalTime(new Date()),
  ];
  switch (filter as TicketsTimeFilter) {
    case "For the last 24 hours":
      start.setDate(end.getDate() - 1);
      break;
    case "Today":
      start.setHours(0, 0, 0, 1);
      end.setHours(23, 59, 59, 999);
      break;
    case "Custom time period":
      let timePayload = payload as DateInterval;
      start = new Date(timePayload?.startDate);
      end = new Date(timePayload?.endDate);
      break;
  }
  return [convertLocalTime(start).getTime(), convertLocalTime(end).getTime()];
};

export const widgetFilterToSLFilter = (
  filters: (WidgetFilter | FilterDefinition)[] = []
): TicketsFilters => {
  let mapped: TicketsFilters = {};

  const mapValue = (
    key: string,
    filterValue: string | boolean | string[] | number | undefined
  ) => {
    let value: typeof filterValue;
    switch (typeof filterValue) {
      case "boolean":
        value = filterValue;
        break;
      case "string":
        value = Boolean(filterValue) ? filterValue : undefined;
        break;
      case "object":
        Array.isArray(filterValue) && filterValue.every((v) => Boolean(v))
          ? (value = filterValue)
          : (value = undefined);
      default:
        value = filterValue;
        break;
    }
    return getMappedValue(
      mapped,
      key as keyof TicketsFilters,
      value
    ) as TicketsFilters;
  };

  filters.forEach((f) => {
    switch (f.filterType as FilterTypes) {
      case "priority":
        const priorityFilter: string[] = [];
        (Array.isArray(f.payload)
          ? f.payload
          : f.filterValue
          ? [f.filterValue]
          : []
        ).forEach((value) => {
          const priorityValue = priorityMap[value as TicketsPriorityFilter];
          if (priorityValue) priorityFilter.push(priorityValue);
        });
        if (priorityFilter.length > 0)
          mapped = mapValue("priority", priorityFilter);
        break;
      case "status":
        mapped = mapValue(
          "status",
          Array.isArray(f.payload)
            ? f.payload
            : Boolean(f.filterValue)
            ? [f.filterValue]
            : undefined
        );
        break;
      case "purpose":
        const clearedValue: TicketsPurposeFilter = "Cleared Issues";
        mapped = mapValue(
          "cleared",
          Boolean(f.filterValue)
            ? f.filterValue === clearedValue
              ? "true"
              : "false"
            : undefined
        );
        break;
      case "target":
        let { manufacturer, deviceType, category, model } =
          f.payload as TargetFilterPayload;
        mapped = {
          ...mapped,
          ["device.manufacturer"]: manufacturer,
          ["device.model"]: model,
          ["device.type"]: deviceType as string, // ! to be remove
          ["device.category"]: category,
        };
        break;
      case TIME_FILTER_SEARCH_KEY:
        const [start, end] = getTimesForTicketsFilter(
          f.filterValue as TicketsTimeFilter,
          f.payload as DateInterval
        );
        mapped = mapValue("start.after", start);
        mapped = mapValue("end.before", end);
        break;
      case "accountId":
      case "room.id":
        mapped = mapValue(
          f.filterType as keyof TicketsFilters,
          Boolean(f.filterValue) ? f.filterValue : undefined
        );
        break;
      default:
        mapped = mapValue(f.filterType as keyof TicketsFilters, f.filterValue);
        break;
    }
  });

  return mapped;
};

export const widgetFilterToSearchParams = (
  filters?: (WidgetFilter | FilterDefinition)[]
) => {
  const asSL: any = widgetFilterToSLFilter(filters);

  const timeFilter = filters?.find(
    ({ filterType }) => (filterType as FilterTypes) === TIME_FILTER_SEARCH_KEY
  );
  if (timeFilter !== undefined) {
    asSL[TIME_FILTER_SEARCH_KEY] = encodeURI(timeFilter.filterValue);
  }
  if (decodeURI(asSL[TIME_FILTER_SEARCH_KEY]) != "Custom time period") {
    delete asSL["start.after"];
    delete asSL["end.before"];
  }
  return objectToSearchParams(asSL);
};

const SLFilterToWidgetFilter = (
  slFilters: TicketsFilters
): FilterDefinition[] => {
  const filters: FilterDefinition[] = [];
  if (slFilters.priority !== undefined) {
    filters.push({
      filterType: "priority",
      filterValue: "",
      payload: slFilters.priority,
    });
  }
  if (slFilters.status !== undefined) {
    filters.push({
      filterType: "status",
      filterValue: "",
      payload: slFilters.status,
    });
  }
  let targetPayload: TargetFilterPayload = {
    manufacturer: slFilters["device.manufacturer"],
    deviceType: slFilters["device.type"],
    category: slFilters["device.category"],
    model: slFilters["device.model"],
  };
  const targetPayloadValues = Object.entries(targetPayload).filter((x) => x[1]);
  if (targetPayloadValues.length > 0)
    filters.push({
      filterType: "target",
      filterValue: "",
      payload: targetPayload,
    });

  if (slFilters.cleared !== undefined)
    filters.push({
      filterType: "purpose",
      filterValue: slFilters.cleared ? "Cleared Issues" : "Open Issues",
    });
  if (slFilters["start.after"] && slFilters["end.before"]) {
    let customValue: TicketsTimeFilter = "Custom time period";

    filters.push({
      filterType: TIME_FILTER_SEARCH_KEY,
      filterValue: customValue,
      payload: {
        startDate: slFilters["start.after"],
        endDate: slFilters["end.before"],
      },
    });
  }
  if (slFilters["room.location.country"] !== undefined)
    filters.push({
      filterType: "room.location.country",
      filterValue: slFilters["room.location.country"],
    });
  if (slFilters.accountId !== undefined)
    filters.push({
      filterType: "accountId",
      filterValue: Array.isArray(slFilters["accountId"])
        ? slFilters["accountId"][0]
        : slFilters["accountId"],
    });
  if (slFilters["room.location.name"] !== undefined)
    filters.push({
      filterType: "room.location.name",
      filterValue: slFilters["room.location.name"],
    });
  if (slFilters["room.name"] !== undefined)
    filters.push({
      filterType: "room.name",
      filterValue: Array.isArray(slFilters["room.name"])
        ? slFilters["room.name"][0]
        : slFilters["room.name"],
    });
  if (slFilters.id !== undefined)
    filters.push({
      filterType: "id",
      filterValue: slFilters.id,
    });
  if (slFilters.assignedTo !== undefined)
    filters.push({
      filterType: "assignedTo",
      filterValue: slFilters.assignedTo,
    });
  if (slFilters["device.name"] !== undefined)
    filters.push({
      filterType: "device.name",
      filterValue: Array.isArray(slFilters["device.name"])
        ? slFilters["device.name"][0]
        : slFilters["device.name"],
    });
  if (slFilters.subject !== undefined)
    filters.push({
      filterType: "subject",
      filterValue: slFilters.subject,
    });
  if (slFilters["issue.issue"] !== undefined)
    filters.push({
      filterType: "issue.issue",
      filterValue: slFilters["issue.issue"],
    });
  if (slFilters["syncStatus"] !== undefined)
    filters.push({
      filterType: "syncStatus",
      filterValue: slFilters["syncStatus"],
    });
  if (slFilters["talCaseNo"] !== undefined)
    filters.push({
      filterType: "talCaseNo",
      filterValue: slFilters["talCaseNo"],
    });
  if (slFilters["talState"] !== undefined)
    filters.push({
      filterType: "talState",
      filterValue: slFilters["talState"],
    });
  if (slFilters["syncStatus"] !== undefined)
    filters.push({
      filterType: "syncStatus",
      filterValue: slFilters["syncStatus"],
    });
  if (slFilters["details.requester"] !== undefined)
    filters.push({
      filterType: "details.requester",
      filterValue: Array.isArray(slFilters["details.requester"])
        ? slFilters["details.requester"][0]
        : slFilters["details.requester"],
    });
  if (slFilters["meetings.confirmationNumber"] !== undefined)
    filters.push({
      filterType: "meetings.confirmationNumber",
      filterValue: Array.isArray(slFilters["meetings.confirmationNumber"])
        ? slFilters["meetings.confirmationNumber"][0]
        : slFilters["meetings.confirmationNumber"],
    });
  return filters;
};

const searchToSLFilter = (params: URLSearchParams): TicketsFilters => {
  const filters: TicketsFilters = {};
  const temp = {
    priority: params.getAll("priority"),
    status: params.getAll("status"),
    "room.id": params.getAll("room.id"),
    cleared: params.get("cleared"),
    purpose: params.get("purpose"),
    accountId: params.getAll("accountId"),
    "room.location.country": params.get("room.location.country"),
  };
  asString(filters, params, [
    "assignedTo",
    "room.location.name",

    "room.name",
    "id",
    "subject",
    "details.requester",
    "meetings.confirmationNumber",
    "device.type",
    "device.manufacturer",
    "device.model",
    "device.category",
    "device.name",
    "issue.issue",
    "syncStatus",
    "talState",
    "talCaseNo",
  ]);
  asNumber(filters, params, [
    "lastUpdatedDate.from",
    "lastUpdatedDate.to",
    "start.after",
    "end.before",
  ]);
  if (temp.priority.length !== 0) filters.priority = temp.priority;
  if (temp.status.length !== 0) filters.status = temp.status;
  if (temp["room.id"].length !== 0) filters["room.id"] = temp["room.id"];
  if (temp.accountId.length !== 0) filters.accountId = temp.accountId;
  if (temp.cleared !== null) filters.cleared = temp.cleared === "true";
  if (temp.purpose !== null) {
    filters.cleared = temp.purpose === "Cleared Issues";
  }
  if (temp["room.location.country"])
    filters["room.location.country"] = convertCountryName(
      temp["room.location.country"]
    );
  return filters;
};

const searchToWidgetFilter = (params: URLSearchParams): FilterDefinition[] => {
  let definition = SLFilterToWidgetFilter(searchToSLFilter(params));
  let timeFilterType = decodeURI(params.get(TIME_FILTER_SEARCH_KEY) ?? "");

  if (timeFilterType !== "" && timeFilterType !== "Custom time period") {
    definition.push({
      filterType: TIME_FILTER_SEARCH_KEY,
      filterValue: timeFilterType,
    });
  }

  return definition;
};

export default {
  fromDefinition: {
    toSL: widgetFilterToSLFilter,
    toSearchParams: widgetFilterToSearchParams,
  },
  fromSL: {
    toDefinition: SLFilterToWidgetFilter,
    toSearchParams: (t: TicketsFilters) => SLFilterToSearch(t),
  },
  fromSearchParams: {
    toSL: searchToSLFilter,
    toDefinition: searchToWidgetFilter,
  },
};
